import type {ChatMessage} from "~/pages/chat-agent/types/chatTypes";
import {findTurnThoughtIndex, mergeThoughtSections} from "~/pages/chat-agent/utils/adkEventMapping";

/** True when a tool-call bubble shows its response. */
function isAnsweredCall(m: ChatMessage): boolean {
    return m.event_type === 'function_response';
}

/** True when the tool call a function_response answers already shows a response (e.g. loaded from the backend first). */
export function hasFunctionResponse(messages: ChatMessage[], response: ChatMessage): boolean {
    return Boolean(response.callId)
        && messages.some(m => m.callId === response.callId && isAnsweredCall(m));
}

/**
 * Combines a session loaded from the backend with the bot messages streamed into it while it loaded.
 *
 * ADK persists every non-partial event before sending it over SSE, so the snapshot holds the final version
 * of each event it contains; anything it lacks streamed in after it was read and comes after it. Messages
 * are matched by id, which both sides derive from the ADK event (see mapADKEventToChatMessages). A tool
 * bubble keeps the streamed version (response, progress) unless only the snapshot already has the response.
 * A thinking section takes in the streamed thoughts it lacks. A turn has one, keyed by its first thought, so
 * a streamed section of the current turn that began later (e.g. after a databag switch) has another id and
 * goes into the snapshot's section of that turn.
 */
export function mergeSnapshotWithStreamed(snapshot: ChatMessage[], streamed: ChatMessage[]): ChatMessage[] {
    const merged = [...snapshot];
    const turnThoughtIdx = findTurnThoughtIndex(snapshot);
    const streamedTurnStart = streamed.findLastIndex(m => m.type === 'user') + 1;
    const streamedTailId = streamed[streamed.length - 1]?.id;
    let streamingThoughtIdx = -1;
    streamed.forEach((msg, i) => {
        const idx = merged.findIndex(m => m.id === msg.id);
        // A thinking section merges into the one with its id, else into the current turn's.
        const currentTurnIdx = i >= streamedTurnStart ? turnThoughtIdx : -1;
        const sectionIdx = msg.event_type !== 'thought' ? -1 : idx >= 0 ? idx : currentTurnIdx;
        if (sectionIdx >= 0) {
            const section = mergeThoughtSections(merged[sectionIdx], msg);
            // Nothing followed its last thought, which may still be streaming: the next chunk continues it.
            if (section !== merged[sectionIdx] && msg.thoughtTailId === streamedTailId) streamingThoughtIdx = sectionIdx;
            merged[sectionIdx] = section;
        } else if (idx < 0) {
            merged.push(msg);
        } else if (msg.callId && (isAnsweredCall(msg) || !isAnsweredCall(merged[idx]))) {
            merged[idx] = msg;
        }
    });
    if (streamingThoughtIdx >= 0) {
        merged[streamingThoughtIdx] = {...merged[streamingThoughtIdx], thoughtTailId: merged[merged.length - 1].id};
    }
    return merged;
}
